import { Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { Component, OnInit, ChangeDetectionStrategy, ViewChild, TemplateRef } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, map, switchMap, take, timeoutWith } from 'rxjs/operators';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { combineLatest, interval, of } from 'rxjs';
import { IRoutedDialogComponent, RoutedDialogDialogData, RoutedDialogRouteData } from './routed-dialog.module';
import { NavigationDataService } from './navigation-data.service';

@UntilDestroy()
@Component({
    template: '<ng-template><router-outlet></router-outlet></ng-template>',
    styleUrls: ['./routed-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoutedDialogWrapperComponent implements OnInit {
    private dialogRef: MatDialogRef<IRoutedDialogComponent>;
    private routeData: RoutedDialogRouteData;

    maximized = false;

    @ViewChild(TemplateRef, { static: true }) templateRef: TemplateRef<any>;

    constructor(
        private dialog: MatDialog,
        private route: ActivatedRoute,
        private overlay: Overlay,
        private router: Router,
        private breakpoint: BreakpointObserver,
        private navigationData: NavigationDataService,
        private overlayPositionBuilder: OverlayPositionBuilder
    ) {}

    ngOnInit(): void {
        const isMobile$ = this.breakpoint.observe(Breakpoints.Handset).pipe(map((b) => b.matches));

        // open a dialog when coming to this page
        combineLatest([this.route.params, isMobile$])
            .pipe(
                untilDestroyed(this),
                switchMap(async ([_, isMobile]) => {
                    // make sure there are no duplicate dialogs
                    if (!this.route.snapshot.data.fullscreen) {
                        this.closeDialog(false);
                    }

                    // if navigating from one dialog to another, we need to wait here to prevent problems with angular router
                    if (this.route.snapshot.data.fullscreen) {
                        // await this.waitUntilNoDialogs();
                    }

                    // open a new one
                    const route = this.route.snapshot;
                    this.routeData = route.data as RoutedDialogRouteData;
                    const dialogData: RoutedDialogDialogData = {
                        route,
                        route$: this.route,
                        close: () => this.closeDialog(),
                        maximize: () => this.maximizeDialog(),
                        routeData: this.routeData,
                    };
                    if (isMobile) {
                        this.routeData.nonModal = false;
                    }
                    const panelClass: string[] = [
                        'routed-dialog',
                        ...(this.routeData.fullscreen ? ['fullscreen'] : ['']),
                        ...(this.routeData.nonModal ? ['non-modal'] : []),
                        ...(isMobile ? ['mobile'] : ['nonmobile']),
                    ];

                    if (!this.route.snapshot.data.fullscreen || !this.dialogRef) {
                        this.dialogRef = this.dialog.open(this.routeData.dialogComponent, {
                            panelClass,
                            scrollStrategy: this.overlay.scrollStrategies.block(),
                            autoFocus: true,
                            disableClose: true,
                            backdropClass: [
                                'routed-dialog-backdrop',
                                ...(isMobile ? ['mobile'] : ['nonmobile']),
                                ...(this.routeData.fullscreen ? ['fullscreen'] : ['nonfullscreen']),
                                ...(this.routeData.backdropFullscreen ? ['bg-fullscreen'] : []),
                            ],
                            hasBackdrop: !this.routeData.nonModal,
                            data: dialogData,
                            closeOnNavigation: false,
                        });
                    }

                    this.dialogRef.componentInstance.contentTemplate = this.templateRef;

                    /** For small non-fullscreen dialogs, make them always on top (compared to fullscreen dialog) */
                    const overlayRef = (this.dialogRef as any)._ref.overlayRef as OverlayRef;
                    if (overlayRef?.overlayElement) {
                        const p = overlayRef.overlayElement.parentElement;
                        if (p.classList.contains('cdk-global-overlay-wrapper')) {
                            if (this.routeData.fullscreen) {
                                p.classList.add('routed-dialog-fullscreen');
                            } else {
                                p.classList.add('routed-dialog-nonfullscreen');
                            }
                        }
                    }

                    if (!this.routeData.nonModal) {
                        this.dialogRef
                            .backdropClick()
                            .pipe(take(1))
                            .subscribe(() => this.closeDialog());
                    }

                    // perform positioning to dialogLink source element
                    if (this.routeData.nonModal && !isMobile) {
                        this.positionDialogToClick();
                    }

                    // Listen to escape keydown event of specific dialogRef, not the entire DOM
                    // Filter pipe prevents repeating events and accidentally closing multiple dialogs (e.g. pressing and holding the escape key for too long)
                    this.dialogRef
                        .keydownEvents()
                        .pipe(filter((e) => !e.repeat))
                        .subscribe((e) => {
                            if (e.key === 'Escape') {
                                this.closeDialog();
                            }
                        });
                })
            )
            .subscribe();
    }

    async waitUntilNoDialogs() {
        if (this.dialog.openDialogs.length > 0) {
            const origDialogsCount = this.dialog.openDialogs.length;
            await new Promise<void>((resolve) => {
                interval(100)
                    .pipe(
                        filter(() => {
                            // wait for change
                            return this.dialog.openDialogs.length !== origDialogsCount;
                        }),
                        take(1),
                        timeoutWith(5000, of(null)) // to ensure we never get stuck..
                    )
                    .subscribe(() => {
                        resolve();
                    });
            });
        }
    }

    async closeDialog(updateUrl = true) {
        if (this.dialogRef && updateUrl) {
            if (this.route.snapshot.parent.outlet === 'd') {
                this.router.navigate([{ outlets: { d: null } }], { queryParamsHandling: 'merge' });
            } else if (this.route.snapshot.parent.outlet === 'd2') {
                this.router.navigate([{ outlets: { d2: null } }], { queryParamsHandling: 'merge' });
            } else {
                // Redirect to original URL this dialog was opened from
                this.dialogRef?.close();
                this.dialogRef = undefined;

                // work around an Angular issue that local routing did not work after closing a dialog
                await this.waitUntilNoDialogs();
                setTimeout(() => {
                    this.router.navigateByUrl(this.routeData.originalRoute.navigatedFromRoute, {});
                });
            }
        } else {
            this.dialogRef?.close();
            this.dialogRef = undefined;
        }
    }

    ngOnDestroy() {
        // Ensure dialog is closed when navigating away from this page without affecting the already updated url
        this.closeDialog(false);
    }

    /**
     * Position material dialog based on mouse click
     * Use X position of mouse click to place dialog near while limiting Y position
     * Creates dialog with a new origin that forces dialog to appear at the top of the view
     * Prevents dialog overflow by adjusting positioning based on viewport boundaries
     */
    positionDialogToClick() {
        const dataId = window?.history?.state?.dataId;
        if (dataId) {
            const dialogLinkData = this.navigationData.popNavigationData(dataId);
            if (dialogLinkData?.ev) {
                const ev = dialogLinkData.ev as MouseEvent;
                const el = ev.target as HTMLElement;
                const overlayRef = (this.dialogRef as any)._ref.overlayRef as OverlayRef;
                const overlayOrigin = { x: ev.clientX, y: Math.min(ev.clientY, 100) };

                // Get viewport dimensions
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;

                // Calculate safe margins from screen edges
                const safeMargin = 20;
                const baseOffset = 40;

                // Determine if we have enough space on left/right sides
                const spaceOnLeft = ev.clientX;
                const spaceOnRight = viewportWidth - ev.clientX;

                // Prefer positioning based on available space
                const preferRight = spaceOnLeft > spaceOnRight;

                const pos = this.overlayPositionBuilder
                    .flexibleConnectedTo(el)
                    .withPush(true)
                    .withFlexibleDimensions(true)
                    .withViewportMargin(safeMargin)
                    .setOrigin(overlayOrigin)
                    .withPositions([
                        // Primary positions based on available space
                        ...(preferRight
                            ? [
                                  {
                                      // top-right (preferred when more space on right)
                                      originX: 'end' as const,
                                      originY: 'bottom' as const,
                                      overlayX: 'end' as const,
                                      overlayY: 'top' as const,
                                      offsetX: -baseOffset,
                                  },
                                  {
                                      // bottom-right
                                      originX: 'end' as const,
                                      originY: 'top' as const,
                                      overlayX: 'end' as const,
                                      overlayY: 'bottom' as const,
                                      offsetX: -baseOffset,
                                  },
                              ]
                            : [
                                  {
                                      // top-left (preferred when more space on left)
                                      originX: 'start' as const,
                                      originY: 'bottom' as const,
                                      overlayX: 'start' as const,
                                      overlayY: 'top' as const,
                                      offsetX: baseOffset,
                                  },
                                  {
                                      // bottom-left
                                      originX: 'start' as const,
                                      originY: 'top' as const,
                                      overlayX: 'start' as const,
                                      overlayY: 'bottom' as const,
                                      offsetX: baseOffset,
                                  },
                              ]),
                        // Fallback positions
                        {
                            // center-bottom (fallback)
                            originX: 'center' as const,
                            originY: 'bottom' as const,
                            overlayX: 'center' as const,
                            overlayY: 'top' as const,
                            offsetY: 8,
                        },
                        {
                            // center-top (fallback)
                            originX: 'center' as const,
                            originY: 'top' as const,
                            overlayX: 'center' as const,
                            overlayY: 'bottom' as const,
                            offsetY: -8,
                        },
                    ]);

                overlayRef.updatePositionStrategy(pos);
            }
        }
    }
    maximizeDialog() {
        this.maximized = !this.maximized;
        if (this.maximized) {
            this.dialogRef.addPanelClass('fullscreen');
            this.dialogRef.updatePosition({ top: '0', left: '0' });
        } else {
            this.dialogRef.removePanelClass('fullscreen');
        }
    }
}
